import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { trpc } from "@/lib/trpc";
import { Terminal, AlertCircle, RefreshCw, Cpu, Server } from "lucide-react";
import { useState } from "react";

interface BuildInfoPanelProps {
  snapshotData?: {
    app: {
      version: string;
      environment: string;
      uptimeSec: number;
      platform: string;
      node: string;
      timezone: string;
    };
    build?: {
      gitCommit?: string | null;
      buildTimeISO?: string | null;
    };
  };
  generatedAtISO?: string;
  onRefresh?: () => void;
}

export function BuildInfoPanel({ snapshotData, generatedAtISO, onRefresh }: BuildInfoPanelProps) {
  if (!snapshotData) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-destructive" />
            <CardTitle>Build Information</CardTitle>
          </div>
          <CardDescription>Build information unavailable</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Build information is not available from the snapshot.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Terminal className="h-5 w-5" />
            <CardTitle>Build Information</CardTitle>
          </div>
          {onRefresh && (
            <Button
              onClick={onRefresh}
              variant="outline"
              size="sm"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          )}
        </div>
        <CardDescription>
          Application build, runtime, and system information
        </CardDescription>
        {generatedAtISO && (
          <div className="text-xs text-muted-foreground">
            Generated: {new Date(generatedAtISO).toLocaleString()}
          </div>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Runtime Information */}
        <div>
          <div className="font-medium mb-3 flex items-center gap-2">
            <Server className="h-4 w-4" />
            Runtime Environment
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <div className="font-medium text-muted-foreground">App Version</div>
              <div>{snapshotData.app.version || 'unknown'}</div>
            </div>
            <div>
              <div className="font-medium text-muted-foreground">Node.js</div>
              <div>{snapshotData.app.node}</div>
            </div>
            <div>
              <div className="font-medium text-muted-foreground">Platform</div>
              <div>{snapshotData.app.platform}</div>
            </div>
            <div>
              <div className="font-medium text-muted-foreground">Uptime</div>
              <div>{Math.floor(snapshotData.app.uptimeSec / 3600)}h {Math.floor((snapshotData.app.uptimeSec % 3600) / 60)}m</div>
            </div>
          </div>
        </div>

        {/* Build Information */}
        <div>
          <div className="font-medium mb-3 flex items-center gap-2">
            <Cpu className="h-4 w-4" />
            Build Metadata
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Git Commit</span>
              <Badge variant={snapshotData.build?.gitCommit ? "default" : "secondary"}>
                {snapshotData.build?.gitCommit ?
                  snapshotData.build.gitCommit.slice(0, 7) :
                  'Unavailable'
                }
                {!snapshotData.build?.gitCommit && <span className="ml-1" title="Not provided at build time">ℹ️</span>}
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Build Time</span>
              <Badge variant={true ? "default" : "secondary"}>
                {new Date(snapshotData.build?.buildTimeISO || import.meta.env.VITE_BUILD_TIME_ISO || new Date().toISOString()).toLocaleString()}
              </Badge>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Timezone</span>
              <Badge variant="outline">{snapshotData.app.timezone}</Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Environment</span>
              <Badge variant="outline">{snapshotData.app.environment}</Badge>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
